<template>
  <div>
    <h1>Applications</h1>
    <div v-if="isLoadingList">
      <div>
          <h6>Loading list of applications...</h6>
          <b-spinner></b-spinner>
        </div>
    </div>
    <div v-else>
      <h5>You have been assigned {{ listAssignments.length }} applications to review. Click on an application to score.</h5>
      <b-progress class="w-50 mb-2" :value="numberOfScored" :max="listAssignments.length" show-value></b-progress>
      <b-button-group class="mb-4">
        <b-button class="jury-btn" :pressed="isNotFiltered" @click="filterAssignments('all')">All</b-button>
        <b-button class="jury-btn" :pressed="isFilteredByScored" @click="filterAssignments('scored')">Scored</b-button>
        <b-button class="jury-btn" :pressed="isFilteredByUnscored" @click="filterAssignments('unscored')">Unscored</b-button>
      </b-button-group>
    </div>
    <b-list-group>
      <b-list-group-item class="application-item" v-for="assignment in listFilteredAssignments" :key="assignment.i" button @click="scoreApp(assignment.student_volunteer_id)">
        <h4>Application {{ assignment.i }}</h4>
        <div class="scores" v-if="assignment.isScored">
          <span>Essay 1 Score: <b-badge variant="primary">{{ assignment.essay1 }}</b-badge></span>
          <span>Essay 2 Score: <b-badge variant="primary">{{ assignment.essay2 }}</b-badge></span>
          <span>Essay 3 Score: <b-badge variant="primary">{{ assignment.essay3 }}</b-badge></span>
          <span>Overall Approval: <b-badge :variant="assignment.overall ? 'success' : 'warning'">{{ assignment.overall ? 'Yes' : 'No' }}</b-badge></span>
          <span>Red flag: <b-badge :variant="assignment.red_flag ? 'danger' : 'success'">{{ assignment.red_flag ? 'Yes' : 'No' }}</b-badge></span>
          <span v-if="assignment.red_flag && assignment.red_flag_reasons">Reason: {{ assignment.red_flag_reasons }}</span>
        </div>
        <div class="scores" v-else>
          <b-badge variant="warning">Not Scored</b-badge>
        </div>
      </b-list-group-item>
    </b-list-group>
    <!-- Modal Component -->
    <b-modal ref="scoreModal" id="scoreModal" :title="getTitle" size="lg" ok-only ok-title="Close" ok-variant="secondary">
      <div v-if="isLoadingApp" class="content">
        <div class="loader">Loading...</div>
      </div>
      <div style="margin:5px" v-else class="content">
        <b-tabs>
          <b-tab title="Essay 1" active>
            <h5 class="essay-question"> We’d love to hear about the motivations that support your interest in becoming a student
                      volunteer. What excites you most about this opportunity, and what drives you to contribute to the
                      organization in this way? Share how your unique strengths, skills, and experiences would make you
                      an excellent fit for this role.</h5>
            <div class="essay-text">
              <p>{{ activeApp.essay1Content }}</p>
            </div>
          </b-tab>
          <b-tab title="Essay 2">
            <h5 class="essay-question"> Tell about a time when you connected with people from a
                      background different from your own, and how the experience helped you grow or broaden your
                      perspective. Share any meaningful insights or lessons you took away, as well as how it may have
                      influenced your understanding, empathy, or approach to working with others. </h5>
            <div class="essay-text">
              <p>{{ activeApp.essay2Content }}</p>
            </div>
          </b-tab>
          <b-tab title="Essay 3">
            <h5 class="essay-question">Please share about a time when you encountered a stressful or
                      surprising situation involving others, where you took it upon yourself to step in and resolve the
                      issue. What actions did you decide to take, and how did the situation ultimately play out?
                      Reflecting on this experience, what lessons did you gain, and how would you use those insights to
                      navigate similar challenges in the future?</h5>
            <div class="essay-text">
              <p>{{ activeApp.essay3Content }}</p>
            </div>
          </b-tab>
        </b-tabs>
        <b-form inline @submit.prevent="saveScores">
          <b-form-group class="essay-input" label="Essay 1">
            <b-form-select id="essay1-score" v-model="activeApp.essay1Score" :options="scores" required class="mb-1" />
          </b-form-group>
          <b-form-group class="essay-input" label="Essay 2">
            <b-form-select id="essay2-score" v-model="activeApp.essay2Score" :options="scores" required class="mb-1" />
          </b-form-group>
          <b-form-group class="essay-input" label="Essay 3">
            <b-form-select id="essay3-score" v-model="activeApp.essay3Score" :options="scores" required class="mb-1" />
          </b-form-group>
          <b-form-group class="essay-input" label="Overall Approval">
            <b-form-select id="overall-score" v-model="activeApp.overall" :options="overall" required class="mb-1" />
          </b-form-group>
          <b-form-group class="essay-input" label="Red flag">
            <b-form-select id="red_flag" v-model="activeApp.red_flag" :options="red_flag" required class="mb-1" />
          </b-form-group>
          <b-form-group v-if="activeApp.red_flag" class="essay-input" label="Red Flag Reason">
            <b-form-textarea v-model="activeApp.red_flag_reasons" placeholder="Enter reason for red flag..." rows="3" />
          </b-form-group>
          <b-form-group style="margin-left: 3px" label="Confirm">
            <b-button type="submit" :disabled="isSaving" variant="primary">Save</b-button>
            <div v-if="isSaving" class="loader">Saving...</div>
          </b-form-group>
        </b-form>
        <div v-if="showModalMessage" class="alert alert-success" id="modal-alert" role="alert">
          <p>{{ modalMessage }}</p>
        </div>
      </div>
      <div slot="modal-footer">
        <div class="footer-left">
          <b-button :disabled="isFirstApp" @click="previousApp" variant="info">Previous App</b-button>
          <b-button :disabled="isLastApp" @click="nextApp" variant="info">Next App</b-button>
        </div>
        <div class="footer-right">
          <b-button @click="closeModal" variant="secondary">Close</b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'

  export default {
    name: 'ListJuryApplications',
    data () {
      return {
        isLoadingList: false,
        isLoadingApp: false,
        isSaving: false,
        isNotFiltered: true,
        isFilteredByScored: false,
        isFilteredByUnscored: false,
        scores: [
          { value: null, text: 'Score...' },
          { value: 1, text: '1' },
          { value: 2, text: '2' },
          { value: 3, text: '3' },
          { value: 4, text: '4' },
          { value: 5, text: '5' }
        ],
        overall: [
          { value: null, text: 'Select...' },
          { value: 1, text: 'Yes' },
          { value: 0, text: 'No' }
        ],
        red_flag: [
          { value: 0, text: 'No' },
          { value: 1, text: 'Yes' }
        ],
        showModalMessage: false,
        modalMessage: ''
      }
    },
    computed: {
      ...mapGetters('jury', ['listAssignments']),
      ...mapState('jury', ['assignments', 'activeApp']),
      listFilteredAssignments () {
        if (this.isNotFiltered) {
          return this.listAssignments
        } else {
          if (this.isFilteredByScored) {
            return this.listAssignments.filter(assignment => {
              return assignment.isScored
            })
          } else {
            return this.listAssignments.filter(assignment => {
              return !assignment.isScored
            })
          }
        }
      },
      numberOfScored () {
        return this.listAssignments.filter(assignment => {
          return assignment.isScored
        }).length
      },
      getTitle () {
        if (this.isLoadingApp) {
          return 'Loading...'
        } else {
          return 'Score Application ' + this.activeApp.i
        }
      },
      isFirstApp () {
        return this.activeApp.i === 1
      },
      isLastApp () {
        return this.activeApp.essay1Score === null ||
          this.assignments[this.activeApp.i - 1].essay1 !== this.activeApp.essay1Score ||
          this.activeApp.essay2Score === null ||
          this.assignments[this.activeApp.i - 1].essay2 !== this.activeApp.essay2Score ||
          this.activeApp.essay3Score === null ||
          this.assignments[this.activeApp.i - 1].essay3 !== this.activeApp.essay3Score ||
          this.activeApp.overall === null ||
          this.assignments[this.activeApp.i - 1].overall !== this.activeApp.overall ||
          this.activeApp.red_flag === null ||
          this.assignments[this.activeApp.i - 1].red_flag !== this.activeApp.red_flag ||
          this.activeApp.i === this.assignments.length
      }
    },
    methods: {
      filterAssignments (filter) {
        switch (filter) {
          case 'all':
            this.isNotFiltered = true
            this.isFilteredByScored = false
            this.isFilteredByUnscored = false
            break
          case 'scored':
            this.isNotFiltered = false
            this.isFilteredByScored = true
            this.isFilteredByUnscored = false
            break
          case 'unscored':
            this.isNotFiltered = false
            this.isFilteredByScored = false
            this.isFilteredByUnscored = true
            break
        }
      },
      scoreApp (svId) {
        this.isLoadingApp = true
        this.$refs.scoreModal.show()
        this.$store.dispatch('jury/setActiveApp', svId).then(() => {
          this.isLoadingApp = false
        }).catch(err => {
          this.isLoadingApp = false
          console.log(err)
        })
      },
      saveScores () {
        this.isSaving = true
        this.$store.dispatch('jury/saveScores').then(() => {
          this.isSaving = false
          this.modalMessage = 'Scores saved successfully'
          this.showModalMessage = true
          setTimeout(() => {
            this.showModalMessage = false
            this.modalMessage = ''
          }, 3000)
        })
      },
      previousApp () {
        this.scoreApp(this.assignments[this.activeApp.i - 2].student_volunteer_id)
      },
      nextApp () {
        this.scoreApp(this.assignments[this.activeApp.i].student_volunteer_id)
      },
      closeModal () {
        this.$refs.scoreModal.hide()
      }
    },
    created: function () {
      if (this.$store.state.jury.assignments.length === 0) {
        this.isLoadingList = true
        this.$store.dispatch('jury/getTLAssignments').then(() => {
          this.isLoadingList = false
        }).catch(err => {
          console.log(err)
          this.isLoadingList = false
        })
      }
    }
  }
</script>

<style lang="scss">
  .modal-backdrop {
    background-color: rgba(0,0,0,0.5);;
  }
    .jury-btn  {
    color: white;
    background-color: #F1EFE3;
    border-color: #F1EFE3;

    &:focus {
      box-shadow: none !important;
    }
    // &:hover:not([disabled="disabled"]) {
    //   background-color: #F1EFE3;
    // }
    &:not(:disabled):not(.disabled).active {
      color: black;
      background-color: #F1EFE3;
      // background-origin: content-box;
      // background-repeat: no-repeat;
    }
  }
  .application-item {
    font-size: 1.2em;
  }
  .scores {
    display: inline;
    span {
      margin: 0px 10px;
    }
  }
  .modal-footer {
    display: block !important;
    .footer-left {
      text-align: left;
      display: inline-block;
    }
    .footer-right {
      text-align: right;
      float: right;
    }
  }
  .essay-question, .essay-text {
    text-align: justify;
    margin: 15px 15px 20px 10px;
  }
  .essay-input {
    margin: 5px;
  }
  #scoreModal {
    .tabs {
      .nav-link {
        color: #007bff;
        &:hover {
          color: #0056b3;
        }
      }
      .nav-link.active {
        color: #495057;
      }  
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/jury/ListJuryApplications.vue